import { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';
import { IconCamera, IconX, IconKeyboard } from '@tabler/icons-react';

export default function QRScanner({ onScanSuccess, onClose, isOpen }) {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const lastScanRef = useRef(0);
  const onScanSuccessRef = useRef(onScanSuccess);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);

  // Keep ref up-to-date so QrScanner callback always calls the latest version
  useEffect(() => {
    onScanSuccessRef.current = onScanSuccess;
  }, [onScanSuccess]);

  useEffect(() => {
    if (isOpen && !showManualInput) {
      startScanner();
    }
    return () => {
      destroyScanner();
    };
  }, [isOpen, showManualInput]);

  const startScanner = async () => {
    if (!videoRef.current) return;
    try {
      scannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          const now = Date.now();
          if (now - lastScanRef.current < 1500) return;
          lastScanRef.current = now;
          new Audio('/scan-beep.mp3').play().catch(() => {});
          onScanSuccessRef.current(result.data);
        },
        {
          preferredCamera: 'environment',
          highlightScanRegion: true,
          highlightCodeOutline: true,
        }
      );
      await scannerRef.current.start();
      setIsScanning(true);
      setError('');
    } catch {
      setError('Gagal mengakses kamera. Pastikan izin kamera diberikan.');
      setIsScanning(false);
    }
  };

  const destroyScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop();
      scannerRef.current.destroy();
      scannerRef.current = null;
      setIsScanning(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      onScanSuccess(manualInput.trim());
      setManualInput('');
    }
  };

  const toggleManualInput = () => {
    if (!showManualInput) destroyScanner();
    setShowManualInput(!showManualInput);
    setError('');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
            Scan QR Code
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors"
          >
            <IconX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {!showManualInput ? (
            <>
              {/* Video Scanner */}
              <div className="relative rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 aspect-square">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                />
                {isScanning && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-52 h-52 relative">
                      <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-amber-500 rounded-tl-lg" />
                      <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-amber-500 rounded-tr-lg" />
                      <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-amber-500 rounded-bl-lg" />
                      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-amber-500 rounded-br-lg" />
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <button
                onClick={toggleManualInput}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium transition-colors"
              >
                <IconKeyboard className="w-5 h-5" />
                <span>Input Manual</span>
              </button>
            </>
          ) : (
            <>
              <form onSubmit={handleManualSubmit}>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                  Masukkan Kode Barang
                </label>
                <input
                  type="text"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="Scan atau ketik kode barang..."
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 transition-all outline-none"
                  autoFocus
                />
                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={toggleManualInput}
                    className="flex-1 px-4 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    disabled={!manualInput.trim()}
                    className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 dark:disabled:text-slate-400 rounded-lg text-white font-bold transition-colors"
                  >
                    Proses
                  </button>
                </div>
              </form>

              <button
                onClick={toggleManualInput}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium transition-colors"
              >
                <IconCamera className="w-5 h-5" />
                <span>Kembali ke Scanner</span>
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
            Arahkan kamera ke QR code pada bandrol harga barang
          </p>
        </div>
      </div>
    </div>
  );
}
